import { getUser } from "@newsenec/auth-provider";
import { MicroFrontendMetadata } from "@newsenec/metadata-gen";
import { isFeatureActiveAsync } from "./feature-toggles";
import { fetchAllMetadata } from "@newsenec-lib/micro-frontend-utils";

/** Maps each cache key (composite of role and market) to the list of allowed microfrontend names. */
const dataCache = new Map<string, string[]>();

/**
 * Returns the names of all allowed micro frontends depending on the logged-in user. Caches return value.
 */
export async function getAllowedMicroFrontends(market: string = "DE"): Promise<string[]> {
    const roles = await getUserRoles();

    const cacheKey = roles.length === 0 ? `cache_NO_ROLES_${market}` : `cache_${roles}_${market}`;
    if (dataCache.has(cacheKey)) {
        return dataCache.get(cacheKey) ?? [];
    }

    const allMetadata = await fetchAllMetadata();
    const allowedMetadata = await filterMicroFrontends(allMetadata, roles, market);
    const allowedMfs = allowedMetadata.map((metadata) => metadata.name);
    dataCache.set(cacheKey, allowedMfs);
    return allowedMfs;
}

/**
 * Resets the cache for allowed micro frontends.
 */
export function resetAllowedMicroFrontendsCache() {
    dataCache.clear();
}

async function getUserRoles(): Promise<string[]> {
    try {
        const user = await getUser();
        return user?.keycloakRoles ?? [];
    } catch (e) {
        console.log("Unauthenticated. Could not get roles.", e);
        return [];
    }
}

async function filterMicroFrontends(
    allMetadata: MicroFrontendMetadata[],
    roles: string[],
    market: string,
): Promise<MicroFrontendMetadata[]> {
    const allowedMetadata: MicroFrontendMetadata[] = [];
    for (const metadata of allMetadata) {
        const isAllowed = await isMicroFrontendAllowed(metadata, roles, market);
        if (isAllowed) allowedMetadata.push(metadata);
    }
    return allowedMetadata;
}

/** @returns always resolves */
async function isMicroFrontendAllowed(metaData: MicroFrontendMetadata, roles: string[], market: string): Promise<boolean> {
    for (const featureToggle of metaData.featureToggle ?? []) {
        if (!(await isFeatureActiveAsync(featureToggle))) return false;
    }

    if (
        metaData.allowedMarkets === undefined ||
        metaData.allowedMarkets === "PUBLIC" ||
        metaData.allowedMarkets.some((visibleMarket: string) => visibleMarket.startsWith(market))
    ) {
        return (
            metaData.visibility === undefined ||
            metaData.visibility === "PUBLIC" ||
            metaData.visibility.some((visibilityRole: string) => roles.includes(visibilityRole))
        );
    }
    return false;
}
